/**
 * SplitPanelContextMenu
 *
 * Context menu for split view panel headers.
 * Provides options to:
 * - Open in new browser tab
 * - Open in new browser window
 * - Move to another tab (with submenu for tab selection)
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaExternalLinkAlt,
  FaWindowRestore,
  FaArrowRight,
  FaPlus,
  FaFile,
} from "react-icons/fa";
import {
  SplitPanel,
  useTabs,
  useCurrentTabId,
} from "@/hooks/use-tabs-store";
import { ContextMenuItem, ContextMenuDivider } from "../arc-sidebar/context-menu-helpers";
import { MotionDiv } from "../arc-sidebar/motion-types";
import { Z_INDEX } from "@/utils/z-index";

interface SplitPanelContextMenuProps {
  /** Position to render the menu */
  position: { x: number; y: number };
  /** The panel this menu is for */
  panel: SplitPanel;
  /** Callback when menu is closed */
  onClose: () => void;
  /** Callback when "Open in new tab" is clicked */
  onOpenInNewTab: () => void;
  /** Callback when "Open in new window" is clicked */
  onOpenInNewWindow: () => void;
  /** Callback when panel should be moved to a tab */
  onMovePanelToTab: (tabId: string | "new") => void;
}

const SplitPanelContextMenu: React.FC<SplitPanelContextMenuProps> = ({
  position,
  panel,
  onClose,
  onOpenInNewTab,
  onOpenInNewWindow,
  onMovePanelToTab,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showTabsSubmenu, setShowTabsSubmenu] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState<"right" | "left">("right");

  // Tabs state
  const tabs = useTabs();
  const currentTabId = useCurrentTabId();

  // Adjust position to stay within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 240),
    y: Math.min(position.y, window.innerHeight - 250),
  };

  // Handle open in new tab
  const handleOpenInNewTab = useCallback(() => {
    onOpenInNewTab();
    onClose();
  }, [onOpenInNewTab, onClose]);

  // Handle open in new window
  const handleOpenInNewWindow = useCallback(() => {
    onOpenInNewWindow();
    onClose();
  }, [onOpenInNewWindow, onClose]);

  // Handle move to tab
  const handleMoveToTab = useCallback(
    (tabId: string | "new") => {
      onMovePanelToTab(tabId);
      onClose();
    },
    [onMovePanelToTab, onClose]
  );

  // Calculate submenu position based on available space
  const handleShowTabsSubmenu = useCallback(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const spaceOnRight = window.innerWidth - rect.right;
      setSubmenuPosition(spaceOnRight > 220 ? "right" : "left");
    }
    setShowTabsSubmenu(true);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showTabsSubmenu) {
          setShowTabsSubmenu(false);
        } else {
          onClose();
        }
        return;
      }

      const isMeta = e.metaKey || e.ctrlKey;

      if (isMeta) {
        switch (e.key.toLowerCase()) {
          case "t":
            e.preventDefault();
            handleOpenInNewTab();
            break;
          case "n":
            if (e.shiftKey) {
              e.preventDefault();
              handleOpenInNewWindow();
            }
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, handleOpenInNewTab, handleOpenInNewWindow, showTabsSubmenu]);

  // Filter out current tab from the list
  const otherTabs = tabs.filter((t) => t.id !== currentTabId);

  return (
    <MotionDiv
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className="fixed bg-surface-elevated rounded-lg shadow-xl border border-arc-border py-1 min-w-[220px]"
      style={{
        top: adjustedPosition.y,
        left: adjustedPosition.x,
        zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU,
      }}
    >
      {/* Open in new tab */}
      <ContextMenuItem onClick={handleOpenInNewTab} shortcut="⌘T">
        <FaExternalLinkAlt className="text-blue-400" /> Ouvrir dans un nouvel onglet
      </ContextMenuItem>

      {/* Open in new window */}
      <ContextMenuItem onClick={handleOpenInNewWindow} shortcut="⇧⌘N">
        <FaWindowRestore className="text-purple-400" /> Ouvrir dans une nouvelle fenêtre
      </ContextMenuItem>

      <ContextMenuDivider />

      {/* Move to tab - with submenu */}
      <div
        className="relative"
        onMouseEnter={handleShowTabsSubmenu}
        onMouseLeave={() => setShowTabsSubmenu(false)}
      >
        <ContextMenuItem hasSubmenu>
          <FaArrowRight className="text-emerald-400" /> Déplacer vers un tab...
        </ContextMenuItem>

        {/* Tabs submenu */}
        <AnimatePresence>
          {showTabsSubmenu && (
            <MotionDiv
              initial={{ opacity: 0, x: submenuPosition === "right" ? -10 : 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: submenuPosition === "right" ? -10 : 10 }}
              className="absolute top-0 bg-surface-elevated rounded-lg shadow-xl border border-arc-border py-1 min-w-[180px]"
              style={{
                [submenuPosition === "right" ? "left" : "right"]: "100%",
                marginLeft: submenuPosition === "right" ? "4px" : undefined,
                marginRight: submenuPosition === "left" ? "4px" : undefined,
                zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU + 1,
              }}
            >
              {/* Create new tab option */}
              <ContextMenuItem onClick={() => handleMoveToTab("new")}>
                <FaPlus className="text-accent" /> Nouveau tab
              </ContextMenuItem>

              {otherTabs.length > 0 && <ContextMenuDivider />}

              {/* Existing tabs */}
              {otherTabs.map((tab) => (
                <ContextMenuItem
                  key={tab.id}
                  onClick={() => handleMoveToTab(tab.id)}
                >
                  <FaFile className="text-content-tertiary" /> {tab.name}
                </ContextMenuItem>
              ))}

              {otherTabs.length === 0 && tabs.length <= 1 && (
                <div className="px-3 py-2 text-xs text-content-tertiary">
                  Aucun autre tab disponible
                </div>
              )}
            </MotionDiv>
          )}
        </AnimatePresence>
      </div>
    </MotionDiv>
  );
};

SplitPanelContextMenu.displayName = "SplitPanelContextMenu";

export default SplitPanelContextMenu;
